import React from "react";
import { InlineMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function formatNumber(value) {
  return value < 0 ? `(${value})` : `${value}`;
}

function operationLatex(data) {
  return `${formatNumber(data.a)}:${formatNumber(data.b)}`;
}

function makeTask(type, a, b, note) {
  return {
    type,
    data: {
      a,
      b,
      result: a / b,
      note,
    },
    tags: ["liczby_calkowite", "dzielenie"],
  };
}

function positiveByPositive() {
  const b = randInt(2, 9);
  const result = randInt(2, 9);
  const a = b * result;
  return makeTask(
    "IntegerDivPositiveByPositiveTask",
    a,
    b,
    "To przypomnienie dzielenia liczb naturalnych: dwie liczby dodatnie dają dodatni iloraz."
  );
}

function negativeByPositive() {
  const b = randInt(2, 9);
  const result = randInt(2, 9);
  const a = -(b * result);
  return makeTask(
    "IntegerDivNegativeByPositiveTask",
    a,
    b,
    "Liczba ujemna podzielona przez dodatnią daje wynik ujemny."
  );
}

function positiveByNegative() {
  const bAbs = randInt(2, 9);
  const resultAbs = randInt(2, 9);
  const a = bAbs * resultAbs;
  const b = -bAbs;
  return makeTask(
    "IntegerDivPositiveByNegativeTask",
    a,
    b,
    "Liczba dodatnia podzielona przez ujemną daje wynik ujemny."
  );
}

function negativeByNegative() {
  const bAbs = randInt(2, 9);
  const resultAbs = randInt(2, 9);
  const a = -(bAbs * resultAbs);
  const b = -bAbs;
  return makeTask(
    "IntegerDivNegativeByNegativeTask",
    a,
    b,
    "Dwie liczby ujemne w dzieleniu dają wynik dodatni."
  );
}

function twoDigitByOneDigitSigned() {
  const bAbs = randInt(2, 9);
  const resultAbs = randInt(4, 18);
  const aAbs = bAbs * resultAbs;
  const variant = randInt(0, 2);
  const a = variant === 0 || variant === 2 ? -aAbs : aAbs;
  const b = variant === 1 || variant === 2 ? -bAbs : bAbs;
  return makeTask(
    "IntegerDivTwoDigitByOneDigitSignedTask",
    a,
    b,
    "Najpierw dzielimy wartości bez znaków, a potem ustalamy znak wyniku."
  );
}

function twoDigitByTwoDigitSigned() {
  const bAbs = randInt(12, 24);
  const resultAbs = randInt(2, 8);
  const aAbs = bAbs * resultAbs;
  const variant = randInt(0, 2);
  const a = variant === 0 || variant === 2 ? -aAbs : aAbs;
  const b = variant === 1 || variant === 2 ? -bAbs : bAbs;
  return makeTask(
    "IntegerDivLargerByTwoDigitSignedTask",
    a,
    b,
    "To trudniejszy wariant: dzielnik jest dwucyfrowy, a znak zależy od liczby minusów."
  );
}

export function generateTasks() {
  return [
    positiveByPositive(),
    negativeByPositive(),
    positiveByNegative(),
    negativeByNegative(),
    twoDigitByOneDigitSigned(),
    twoDigitByTwoDigitSigned(),
  ];
}

export function renderTask({ data }) {
  return (
    <p>
      Oblicz: <InlineMath math={operationLatex(data)} />.
    </p>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <FractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label="wynik"
      placeholder="np. -6"
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  const value = userAnswer?.parsed;
  return Number.isFinite(value) && value === data.result;
}

export function renderAnswer({ data }) {
  return <InlineMath math={`${data.result}`} />;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return <i>(brak odpowiedzi)</i>;
  }
  return <InlineMath math={formatLatexPreview(userAnswer.raw)} />;
}

function explanationSteps(data) {
  const absA = Math.abs(data.a);
  const absB = Math.abs(data.b);
  const absResult = absA / absB;

  if (data.a > 0 && data.b > 0) {
    return [
      <span>
        Dzielimy jak w liczbach naturalnych:{" "}
        <InlineMath math={`${data.a}:${data.b}=${data.result}`} />.
      </span>,
    ];
  }

  if (data.a < 0 && data.b < 0) {
    return [
      <span>
        Najpierw dzielimy wartości bez znaków:{" "}
        <InlineMath math={`${absA}:${absB}=${absResult}`} />.
      </span>,
      <span>Dwa minusy w dzieleniu dają plus, więc wynik jest dodatni.</span>,
    ];
  }

  return [
    <span>
      Najpierw dzielimy wartości bez znaków:{" "}
      <InlineMath math={`${absA}:${absB}=${absResult}`} />.
    </span>,
    <span>Jeden z elementów dzielenia jest ujemny, więc iloraz jest ujemny.</span>,
  ];
}

export function renderExplanation({ data }) {
  const steps = explanationSteps(data);
  return (
    <div style={{ textAlign: "left" }}>
      <p>{data.note}</p>
      {steps.map((step, index) => (
        <p key={index}>{step}</p>
      ))}
      <p>
        <strong>Wynik:</strong>{" "}
        <InlineMath math={`${operationLatex(data)}=${data.result}`} />.
      </p>
    </div>
  );
}

const taskAdapter = {
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderUserAnswer,
  renderExplanation,
};

export const registry = {
  IntegerDivPositiveByPositiveTask: taskAdapter,
  IntegerDivNegativeByPositiveTask: taskAdapter,
  IntegerDivPositiveByNegativeTask: taskAdapter,
  IntegerDivNegativeByNegativeTask: taskAdapter,
  IntegerDivTwoDigitByOneDigitSignedTask: taskAdapter,
  IntegerDivLargerByTwoDigitSignedTask: taskAdapter,
};
